import { useMemo, useState } from 'react';
import { Button, Group, InlineError, Input, Modal, Select, Stack, Text } from '@wapharma/design-system';
import {
  countryDisplayName,
  formatHint,
  legacyToRegistrations,
  schemeLabel,
  schemesForCountry,
  type LegalRegistration,
  type RegistrationScheme,
} from '@wapharma/domain';
import { listCitiesForCountry, listCountries } from '@wapharma/shared';
import styles from './LegalIdentityFields.module.css';

export type LegalIdentityRepresentative = {
  name: string;
  quality: string;
  email: string;
};

export type LegalIdentityValue = {
  legalName: string;
  addressLine: string;
  postalCode: string;
  city: string;
  /** ISO-2, or a legacy free-text value until the user picks a country. */
  country: string;
  registrations: LegalRegistration[];
  /** First row is the primary convention signatory. */
  representatives: LegalIdentityRepresentative[];
};

export const EMPTY_LEGAL_IDENTITY: LegalIdentityValue = {
  legalName: '',
  addressLine: '',
  postalCode: '',
  city: '',
  country: 'MA',
  registrations: [],
  representatives: [{ name: '', quality: '', email: '' }],
};

export function legalIdentityFromRecord(row: {
  legalName?: string | null;
  addressLine?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  registrations?: unknown;
  representatives?: unknown;
  rcNumber?: string | null;
  iceNumber?: string | null;
  ice?: string | null;
  taxId?: string | null;
}): LegalIdentityValue {
  const fromList = Array.isArray(row.registrations)
    ? (row.registrations as LegalRegistration[]).filter(
        (r) => r && typeof r.scheme === 'string' && typeof r.value === 'string',
      )
    : [];
  const registrations =
    fromList.length > 0
      ? fromList
      : legacyToRegistrations({
          rcNumber: row.rcNumber,
          iceNumber: row.iceNumber ?? row.ice,
          taxId: row.taxId,
        });
  const representatives = Array.isArray(row.representatives)
    ? (row.representatives as Array<{ name?: unknown; quality?: unknown; email?: unknown }>)
        .map((rep) => ({
          name: String(rep?.name ?? '').trim(),
          quality: String(rep?.quality ?? '').trim(),
          email: String(rep?.email ?? '').trim(),
        }))
        .filter((rep) => rep.name || rep.quality || rep.email)
    : [];
  return {
    legalName: row.legalName ?? '',
    addressLine: row.addressLine ?? '',
    postalCode: row.postalCode ?? '',
    city: row.city ?? '',
    country: row.country?.trim() || 'MA',
    registrations,
    representatives: representatives.length
      ? representatives
      : [{ name: '', quality: '', email: '' }],
  };
}

export function legalIdentityPayload(
  value: LegalIdentityValue,
  options?: { includeRepresentatives?: boolean },
) {
  const registrations = value.registrations
    .map((row) => ({
      scheme: row.scheme,
      value: row.value.trim(),
      ...(row.label?.trim() ? { label: row.label.trim() } : {}),
    }))
    .filter((row) => row.value);
  const base = {
    legalName: value.legalName.trim() || null,
    addressLine: value.addressLine.trim() || null,
    postalCode: value.postalCode.trim() || null,
    city: value.city.trim() || null,
    country: value.country.trim() || null,
    registrations,
  };
  if (!options?.includeRepresentatives) return base;
  const representatives = value.representatives
    .map((row) => ({
      name: row.name.trim(),
      quality: row.quality.trim(),
      ...(row.email.trim() ? { email: row.email.trim() } : {}),
    }))
    .filter((row) => row.name && row.quality);
  return { ...base, representatives };
}

type Props = {
  value: LegalIdentityValue;
  onChange: (next: LegalIdentityValue) => void;
  disabled?: boolean;
  legalNamePlaceholder?: string;
  /** Wizard splits place (country first) from the country-specific numbers. */
  part?: 'all' | 'place' | 'legal';
  /** WaPharma organization only — suppliers use the contacts table instead. */
  showRepresentatives?: boolean;
};

function upsertScheme(
  rows: LegalRegistration[],
  scheme: RegistrationScheme,
  nextValue: string,
): LegalRegistration[] {
  const rest = rows.filter((r) => r.scheme !== scheme);
  if (!nextValue) return rest;
  return [...rest, { scheme, value: nextValue }];
}

function PlaceFields({
  value,
  onChange,
  disabled,
}: {
  value: LegalIdentityValue;
  onChange: (partial: Partial<LegalIdentityValue>) => void;
  disabled?: boolean;
}) {
  const countryOptions = useMemo(() => {
    const options = listCountries().map((c) => ({ value: c.code, label: c.label }));
    if (value.country && !options.some((o) => o.value === value.country)) {
      options.unshift({ value: value.country, label: value.country });
    }
    return options;
  }, [value.country]);
  const cityOptions = useMemo(() => {
    const options = listCitiesForCountry(value.country).map((c) => ({
      value: c.value,
      label: c.label,
    }));
    if (value.city && !options.some((o) => o.value === value.city)) {
      options.unshift({ value: value.city, label: value.city });
    }
    return options;
  }, [value.country, value.city]);

  const setCountry = (code: string) => {
    const nextCities = listCitiesForCountry(code);
    const keep =
      nextCities.length === 0 || nextCities.some((c) => c.value === value.city);
    onChange({ country: code, city: keep ? value.city : '' });
  };

  return (
    <div className={styles.placeGrid}>
      <Select
        label="Pays"
        options={countryOptions}
        value={value.country}
        disabled={disabled}
        onChange={(e) => setCountry(e.currentTarget.value)}
      />
      {cityOptions.length > 0 ? (
        <Select
          label="Ville"
          options={cityOptions}
          value={value.city}
          disabled={disabled || !value.country}
          onChange={(e) => onChange({ city: e.currentTarget.value })}
        />
      ) : (
        <Input
          label="Ville"
          value={value.city}
          disabled={disabled || !value.country}
          onChange={(e) => onChange({ city: e.currentTarget.value })}
        />
      )}
      <Input
        label="Adresse"
        value={value.addressLine}
        disabled={disabled}
        onChange={(e) => onChange({ addressLine: e.currentTarget.value })}
      />
      <Input
        label="Code postal"
        value={value.postalCode}
        disabled={disabled}
        onChange={(e) => onChange({ postalCode: e.currentTarget.value })}
      />
    </div>
  );
}

export function LegalIdentityFields({
  value,
  onChange,
  disabled,
  legalNamePlaceholder,
  part = 'all',
  showRepresentatives = false,
}: Props) {
  const [identifierOpen, setIdentifierOpen] = useState(false);
  const [identifierLabel, setIdentifierLabel] = useState('');
  const [identifierValue, setIdentifierValue] = useState('');
  const [identifierError, setIdentifierError] = useState<string | null>(null);

  const shown = new Set(schemesForCountry(value.country).map((s) => s.scheme));
  const extras = value.registrations.filter(
    (r) => r.scheme !== 'OTHER' && !shown.has(r.scheme),
  );
  const others = value.registrations.filter((r) => r.scheme === 'OTHER' && (r.value.trim() || r.label?.trim()));

  const patch = (partial: Partial<LegalIdentityValue>) => onChange({ ...value, ...partial });

  const closeIdentifier = () => {
    setIdentifierOpen(false);
    setIdentifierLabel('');
    setIdentifierValue('');
    setIdentifierError(null);
  };

  const saveIdentifier = () => {
    const label = identifierLabel.trim();
    const number = identifierValue.trim();
    if (!label || !number) {
      setIdentifierError('Libellé et numéro sont requis.');
      return;
    }
    patch({
      registrations: [...value.registrations, { scheme: 'OTHER', value: number, label }],
    });
    closeIdentifier();
  };

  const place = part === 'all' || part === 'place';
  const legal = part === 'all' || part === 'legal';
  const countryName = countryDisplayName(value.country);

  const legalNameField = (
    <Input
      label="Raison sociale légale"
      value={value.legalName}
      disabled={disabled}
      placeholder={legalNamePlaceholder}
      onChange={(e) => patch({ legalName: e.currentTarget.value })}
    />
  );

  const schemeFields = schemesForCountry(value.country).map((scheme) => {
    const current = value.registrations.find((r) => r.scheme === scheme.scheme)?.value ?? '';
    const mismatch = formatHint(scheme.scheme, current);
    return (
      <Input
        key={scheme.scheme}
        label={scheme.label}
        value={current}
        disabled={disabled}
        hint={mismatch ?? scheme.hint ?? undefined}
        onChange={(e) =>
          patch({
            registrations: upsertScheme(value.registrations, scheme.scheme, e.currentTarget.value),
          })
        }
      />
    );
  });

  const extraFields = extras.map((row) => (
    <Input
      key={row.scheme}
      label={schemeLabel(row.scheme)}
      value={row.value}
      disabled={disabled}
      onChange={(e) =>
        patch({
          registrations: upsertScheme(value.registrations, row.scheme, e.currentTarget.value),
        })
      }
    />
  ));

  const otherRows = others.map((row, index) => (
    <Group key={`other-${row.label ?? ''}-${index}`} gap="sm" align="flex-end" grow className={styles.span2}>
      <Input
        label="Libellé"
        value={row.label ?? ''}
        disabled={disabled}
        onChange={(e) => {
          const next = others.map((item, i) =>
            i === index ? { ...item, label: e.currentTarget.value } : item,
          );
          patch({
            registrations: [
              ...value.registrations.filter((r) => r.scheme !== 'OTHER'),
              ...next,
            ],
          });
        }}
      />
      <Input
        label="Numéro"
        value={row.value}
        disabled={disabled}
        onChange={(e) => {
          const next = others.map((item, i) =>
            i === index ? { ...item, value: e.currentTarget.value } : item,
          );
          patch({
            registrations: [
              ...value.registrations.filter((r) => r.scheme !== 'OTHER'),
              ...next,
            ],
          });
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="xs"
        disabled={disabled}
        onClick={() =>
          patch({
            registrations: [
              ...value.registrations.filter((r) => r.scheme !== 'OTHER'),
              ...others.filter((_, i) => i !== index),
            ],
          })
        }
      >
        Retirer
      </Button>
    </Group>
  ));

  const addIdentifier = (
    <Group justify="flex-start">
      <Button
        type="button"
        variant="soft"
        size="sm"
        disabled={disabled}
        onClick={() => setIdentifierOpen(true)}
      >
        Ajouter un identifiant
      </Button>
    </Group>
  );

  const primary = value.representatives[0] ?? { name: '', quality: '', email: '' };
  const patchPrimary = (partial: Partial<LegalIdentityRepresentative>) => {
    const next = { ...primary, ...partial };
    patch({
      representatives: [
        next,
        ...value.representatives.slice(1).filter((row) => row.name.trim() || row.quality.trim()),
      ],
    });
  };

  const representativeFields = showRepresentatives ? (
    <>
      <Input
        label="Représentant — nom"
        value={primary.name}
        disabled={disabled}
        onChange={(e) => patchPrimary({ name: e.currentTarget.value })}
      />
      <Input
        label="Représentant — qualité"
        value={primary.quality}
        disabled={disabled}
        placeholder="CEO"
        onChange={(e) => patchPrimary({ quality: e.currentTarget.value })}
      />
      <div className={styles.span2}>
        <Input
          label="Représentant — e-mail"
          value={primary.email}
          disabled={disabled}
          onChange={(e) => patchPrimary({ email: e.currentTarget.value })}
        />
      </div>
    </>
  ) : null;

  const legalGrid = (
    <div className={styles.placeGrid}>
      <div className={styles.span2}>{legalNameField}</div>
      {representativeFields}
      {countryName ? (
        <Text size="xs" c="dimmed" className={styles.span2}>
          Identifiants pour {countryName}.
        </Text>
      ) : null}
      {schemeFields}
      {extraFields}
      {otherRows}
      <div className={styles.span2}>{addIdentifier}</div>
    </div>
  );

  return (
    <Stack gap="sm">
      {part === 'legal' ? legalGrid : null}
      {part === 'all' && legal ? (
        <div className={styles.placeGrid}>
          <div className={styles.span2}>{legalNameField}</div>
          {representativeFields}
        </div>
      ) : null}
      {place ? <PlaceFields value={value} onChange={patch} disabled={disabled} /> : null}
      {part === 'all' && legal ? (
        <Stack gap="sm">
          {schemeFields}
          {extraFields}
          {otherRows}
          {addIdentifier}
        </Stack>
      ) : null}
      <Modal opened={identifierOpen} onClose={closeIdentifier} title="Nouvel identifiant">
        <Stack gap="md">
          {identifierError ? <InlineError>{identifierError}</InlineError> : null}
          <Input
            label="Libellé"
            value={identifierLabel}
            disabled={disabled}
            onChange={(e) => setIdentifierLabel(e.currentTarget.value)}
          />
          <Input
            label="Numéro"
            value={identifierValue}
            disabled={disabled}
            onChange={(e) => setIdentifierValue(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button type="button" variant="ghost" onClick={closeIdentifier}>
              Annuler
            </Button>
            <Button type="button" variant="primary" onClick={saveIdentifier}>
              Ajouter
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
